import { Section } from '@/components/primitives/Section';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Clock, XCircle, Shield, DollarSign, MapPin, Zap, Wrench, ExternalLink, ArrowRight } from 'lucide-react';

export function FAQSection() {
  const faqs = [
    {
      question: 'How fast can you clear my driveway?',
      answer: 'During regular snowfall, most driveways are cleared within 2-4 hours of booking. Storm Pass members get priority matching and faster service during heavy storms.',
      icon: <Clock className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'accent',
    },
    {
      question: 'Do I need a seasonal contract?',
      answer: 'Nope. SNOWBLOWR is 100% pay-per-job. Book when you need it, skip when you don\'t. Or upgrade to Storm Pass for automatic clearing all season long.',
      icon: <XCircle className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'brand',
    },
    {
      question: 'What if the operator misses a spot?',
      answer: 'Every job requires before/after photos. If you\'re not satisfied, you can dispute the completion within 2 hours and we\'ll resolve it—including partial refunds or sending another operator.',
      icon: <Shield className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'accent',
    },
    {
      question: 'How is pricing calculated?',
      answer: 'Price = (base rate by driveway size × snow depth factor × time window factor + add-ons) × surge factor. You see the full breakdown before confirming your booking. No hidden fees.',
      icon: <DollarSign className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'brand',
    },
    {
      question: 'Which cities do you serve?',
      answer: 'We\'re live in Ottawa and Montreal, with Toronto, Calgary, and Edmonton launching winter 2025. Join our waitlist to get notified when we reach your city.',
      icon: <MapPin className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'accent',
    },
    {
      question: 'How do operators get paid?',
      answer: 'Operators earn 75-85% of each job. Payouts happen weekly via Stripe Connect, with instant payout available for a small fee.',
      icon: <Zap className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'brand',
    },
    {
      question: 'What equipment do operators need?',
      answer: 'At minimum, a quality shovel or snowblower. Pro tier operators use two-stage snowblowers or plow trucks for larger driveways and commercial jobs.',
      icon: <Wrench className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'accent',
    },
    {
      question: 'Is there a Storm Pass in my city?',
      answer: 'Storm Pass is rolling out city by city. Join the waitlist at /storm-pass to get notified when it launches in your area.',
      icon: <Zap className="w-5 h-5" strokeWidth={2.5} />,
      accent: 'brand',
    },
  ];

  return (
    <Section
      id="faq"
      eyebrow="Got Questions?"
      title="Everything you need to know"
      subtitle="Can't find what you're looking for? Hit up our support team at hello@snowblowr.com"
      background="frost"
    >
      <div className="max-w-4xl mx-auto mt-12">
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <AccordionItem 
                value={`item-${index}`} 
                className="border-0 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <AccordionTrigger className="text-left font-bold text-neutral-900 hover:text-brand-500 px-6 py-5 hover:no-underline data-[state=open]:bg-gradient-to-r data-[state=open]:from-brand-50 data-[state=open]:to-accent-50 transition-all">
                  <div className="flex items-start gap-4 w-full pr-4">
                    <div className={`mt-0.5 text-${faq.accent}-500 flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      {faq.icon}
                    </div>
                    <span className="text-base sm:text-lg font-black">{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-neutral-700 px-6 pb-6 pt-2">
                  <div className="pl-9">
                    <div className="text-base leading-relaxed font-medium bg-gradient-to-r from-neutral-50 to-white rounded-xl p-5 border-l-4 border-brand-500 shadow-inner">
                      {faq.answer}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 rounded-3xl p-8 sm:p-12 text-center text-white shadow-glow relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:2rem_2rem]" />
            </div>

            <div className="relative z-10">
              <motion.div
                initial={{ scale: 0.9 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/30 backdrop-blur-sm border border-accent-400/50 rounded-full mb-4"
              >
                <Shield className="w-4 h-4 text-accent-400" />
                <span className="text-sm font-bold text-accent-200 uppercase tracking-wider">Still Unsure?</span>
              </motion.div>

              <h3 className="text-3xl sm:text-4xl font-black mb-4">
                We're here to help
              </h3>
              <p className="text-lg sm:text-xl text-snow-100 mb-8 max-w-2xl mx-auto font-medium">
                Our support team responds in <strong className="text-white">&lt; 2 hours</strong>. 
                Seriously—we actually answer.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button
                  variant="secondary"
                  size="lg"
                  className="bg-white text-brand-600 hover:bg-neutral-50 border-0 font-black text-base px-8 py-6 shadow-lg group"
                  onClick={() => window.location.href = '/faq'}
                >
                  View All FAQs
                  <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  className="bg-accent-500 hover:bg-accent-600 text-white border-0 font-black text-base px-8 py-6 shadow-glow-green group"
                  onClick={() => window.location.href = 'mailto:hello@snowblowr.com'}
                >
                  Contact Support
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="mt-10 pt-8 border-t border-white/20 grid grid-cols-3 gap-4 max-w-xl mx-auto">
                <div>
                  <div className="text-3xl font-black text-white mb-1">&lt; 2hr</div>
                  <div className="text-sm text-snow-200 font-medium">Service Time</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-white mb-1">24/7</div>
                  <div className="text-sm text-snow-200 font-medium">Available</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-white mb-1">98%</div>
                  <div className="text-sm text-snow-200 font-medium">Resolved</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

